import {
  Button,
  buttonStylesLink,
  buttonStylesLinkBlack,
  debounce,
  getDescriptionForItemCount,
  ISearchProps,
  PageHeader,
  pageHeaderStylesSecondary,
  Pivot,
  pivotContainerStylesFill,
  pivotStylesFill,
  Search,
  useClassNames,
} from '@h2oai/ui-kit';
import { useCallback, useMemo, useState } from 'react';
import { useHistory } from 'react-router-dom';

import { FeatureSearchResultList } from '../../../components/FeatureSearchResultList/FeatureSearchResultList';
import { FeatureSetList } from '../../../components/FeatureSetList/FeatureSetList';
import { ProjectList } from '../../../components/ProjectList/ProjectList';
import { ListableFeature } from '../../../featurestore/gen/ai/h2o/featurestore/api/v1/CoreService.pb';
import { ContentsTitle } from '../../../featurestore/models';
import { featurestorePageClassName } from '../../../featurestore/styles';
import { useSearchParams } from '../../../utils/hooks';
import { RoutePaths } from '../../Routes';
import { ISearchResultsClassNames, ISearchResultsStyles, searchResultsStylesDefault } from './SearchResults.styles';

export const SearchResults = () => {
  const [params] = useSearchParams();
  const history = useHistory();
  const [searchText, setSearchText] = useState(params.get('query') || '');
  const [description, setDescription] = useState('');
  const changeSearchText = useCallback((text: string) => {
    setSearchText(text);
  }, []);
  const onSearchTextChange = useMemo(() => debounce(300, changeSearchText), [changeSearchText]);
  const onUpdateData = useCallback((count: number, pageSize: number, pageToken: string, name: ContentsTitle) => {
    setDescription(getDescriptionForItemCount(count, pageSize, pageToken, name));
  }, []);
  const onOpenFeaturePage = useCallback(
    (data: ListableFeature) => {
      // TODO: FeatureSetDetails page should handle the featureName to open the FeatureDetails
      history.push(`${RoutePaths.FEATURESTORE_FEATURE_SET}/${data.featureSetId}/${data.name}`);
    },
    [history]
  );
  const searchProps: ISearchProps = {
    placeholder: 'Search by name',
    defaultSearchText: params.get('query') || '',
    onSearchTextChange,
  };
  const classNames = useClassNames<ISearchResultsStyles, ISearchResultsClassNames>(
    'SearchResults',
    searchResultsStylesDefault,
    undefined,
    featurestorePageClassName
  );
  return (
    <div className={classNames.root} data-test="SearchResults">
      <div className={classNames.header}>
        <Button
          styles={[buttonStylesLink, buttonStylesLinkBlack]}
          text="Back"
          iconName="Back"
          onClick={() => history.push(RoutePaths.FEATURESTORE_ROOT)}
        />
      </div>
      <PageHeader
        styles={pageHeaderStylesSecondary}
        pageTitle={`Search results for "${searchText}"`}
        style={{ marginBottom: 16 }}
        secondaryContents={<Search {...searchProps} style={{ width: 641 }} />}
      />
      <Pivot
        styles={pivotStylesFill}
        pivotContainerStyles={pivotContainerStylesFill}
        items={[
          {
            headerText: ContentsTitle.projects,
            content: (
              <div className={classNames.list}>
                <div className={classNames.description}>{description}</div>
                <ProjectList isCardView searchText={searchText} onUpdateData={onUpdateData} />
              </div>
            ),
          },
          {
            headerText: ContentsTitle.featureSets,
            content: (
              <div className={classNames.list}>
                <div className={classNames.description}>{description}</div>
                <FeatureSetList isCardView searchText={searchText} onUpdateData={onUpdateData} />
              </div>
            ),
          },
          {
            headerText: ContentsTitle.features,
            content: (
              <div className={classNames.list}>
                <div className={classNames.description}>{description}</div>
                <FeatureSearchResultList
                  searchText={searchText}
                  onUpdateData={onUpdateData}
                  onOpenFeaturePage={onOpenFeaturePage}
                />
              </div>
            ),
          },
        ]}
      />
    </div>
  );
};
